import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@Pages/pageRuntime/components/ui/dialog/Dialog';
import { cn } from '@Pages/pageRuntime/lib/utils';
import type React from 'react';

type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl';

type ModalProps = {
  /**
   * Controlled open state.
   */
  open?: boolean;

  /**
   * Initial uncontrolled open state.
   */
  defaultOpen?: boolean;

  /**
   * Called when the modal opens or closes.
   */
  onOpenChange?: (open: boolean) => void;

  /**
   * Element that opens the modal.
   */
  trigger?: React.ReactNode;

  /**
   * Modal heading.
   */
  title?: React.ReactNode;

  /**
   * Supporting text below the title.
   */
  description?: React.ReactNode;

  /**
   * Main modal body.
   */
  children: React.ReactNode;

  /**
   * Footer actions. With an escape action (Cancel), wrap them in
   * `<div className="flex w-full items-center justify-between gap-2">`: a ghost Cancel first,
   * then the other actions in one group with the confirming action last, so Cancel sits at
   * the leading edge and the confirming action at the trailing edge. A lone Close button
   * needs no wrapper; the footer keeps it at the trailing edge.
   */
  footer?: React.ReactNode;

  /**
   * Width preset. Defaults to 'md'.
   */
  size?: ModalSize;

  /**
   * Whether the modal can be closed by the user via the close button or escape key.
   */
  isCloseable?: boolean;

  className?: string;
};

const modalSizeClasses: Record<ModalSize, string> = {
  sm: 'sm:max-w-sm',
  md: 'sm:max-w-lg',
  lg: 'sm:max-w-2xl',
  xl: 'sm:max-w-4xl',
  '2xl': 'sm:max-w-6xl'
};

/**
 * A theme-aware dialog for focused content, forms, confirmations, or detail views. Keep
 * modal content concise and usable on small screens.
 *
 * @example
 * <Sdk.Components.Modal title="Student Details" trigger={<Sdk.Components.Button>Open</Sdk.Components.Button>}>
 *   <p>Details go here.</p>
 * </Sdk.Components.Modal>
 */
export function Modal({
  open,
  defaultOpen,
  onOpenChange,
  trigger,
  title,
  description,
  children,
  footer,
  size = 'md',
  isCloseable = true,
  className
}: ModalProps) {
  return (
    <Dialog open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange}>
      {trigger ? <DialogTrigger asChild>{trigger}</DialogTrigger> : null}
      <DialogContent closeable={isCloseable} className={cn(modalSizeClasses[size], className)}>
        {title || description ? (
          <DialogHeader>
            {title ? <DialogTitle>{title}</DialogTitle> : null}
            {description ? <DialogDescription>{description}</DialogDescription> : null}
          </DialogHeader>
        ) : null}
        <div className="min-h-0">{children}</div>
        {footer ? <DialogFooter>{footer}</DialogFooter> : null}
      </DialogContent>
    </Dialog>
  );
}
